<template>
  <div id="site_toolbar">
    <div class="toolbar_left">
      <v-btn :to="backRoute" class="ml-2" prepend-icon="mdi-arrow-left" size="small" variant="text">
        {{ $t('back') }}
      </v-btn>
    </div>
    <div class="toolbar_main">
      <span :title="documentName" class="toolbar_main__name">{{ documentName || 'Untitled template' }}</span>
      <v-fade-transition mode="out-in">
        <span v-if="isDirty" key="dirty" class="toolbar_main__status toolbar_main__status--dirty">
          <span class="toolbar_main__status-dot"></span>
          Unsaved changes
        </span>
        <span v-else key="saved" class="toolbar_main__status">
          <v-icon size="x-small">mdi-check</v-icon>
          Saved
        </span>
      </v-fade-transition>
    </div>
    <div class="toolbar_right">
      <div>
        <v-btn v-tippy content="Keyboard shortcuts" size="small" variant="text" @click="$emit('open-shortcuts')">
          <v-icon size="small">mdi-keyboard-outline</v-icon>
        </v-btn>
      </div>
      <div class="mr-4">
        <v-btn
          v-if="company.uuid"
          :to="sampleRoute"
          class="mr-1"
          prepend-icon="mdi-eye-outline"
          size="small"
          target="_blank"
          variant="text"
        >
          Preview
        </v-btn>
      </div>
      <div class="mr-2">
        <v-btn
          :color="isDirty ? 'primary' : undefined"
          :disabled="!isDirty && !isSaving"
          :loading="isSaving"
          :variant="isDirty ? 'flat' : 'text'"
          prepend-icon="mdi-content-save-outline"
          size="small"
          @click="handleSave"
        >
          {{ $t('save') }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {computed, onMounted, ref} from 'vue';
import {useCompany} from '@/composables/useCompany';
import {store} from '@/store';
import {useRoute} from 'vue-router';

const route = useRoute();

const emit = defineEmits(['toolbarHeight', 'saveTemplate', 'open-shortcuts']);

withDefaults(defineProps<{
  documentName?: string;
  isDirty?: boolean;
}>(), {
  documentName: '',
  isDirty: false,
});

const {company} = useCompany();

const toolbarHeight = ref(0);

const documentLoading = computed(() => store.getters['document/loading']);
const isSaving = computed(() => documentLoading.value.saving);

// Route objects
const sampleRoute = computed(() => ({
  name: 'esign.recipient.v2.sample',
  params: {
    envelope_id: route.params.uuid,
    recipient_id: company.value.uuid,
  },
}));
const backRoute = computed(() => ({name: 'documents.index', hash: '#templates'}));

const handleSave = () => {
  emit('saveTemplate');
};

onMounted(() => {
  const toolbar = document.getElementById('site_toolbar');
  if (toolbar) {
    toolbarHeight.value = toolbar.offsetHeight;
    emit('toolbarHeight', toolbarHeight.value);
  }
});
</script>

<style>
.site-editor #site_toolbar {
  background: rgb(var(--v-theme-surface));
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  display: flex;
  align-items: center;
  min-height: 41px;
  width: 100%;
  flex: 0 0 auto;
}

#site_toolbar .toolbar_left {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
}

#site_toolbar .toolbar_main {
  align-items: center;
  display: flex;
  flex: 1 1 0;
  gap: 10px;
  justify-content: center;
  min-width: 0;
  padding: 0 12px;
}

#site_toolbar .toolbar_main__name {
  font-weight: 500;
  max-width: 40vw;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

#site_toolbar .toolbar_main__status {
  align-items: center;
  color: rgba(var(--v-theme-on-surface), 0.55);
  display: inline-flex;
  font-size: 12px;
  gap: 4px;
  white-space: nowrap;
}

#site_toolbar .toolbar_main__status--dirty {
  color: rgb(var(--v-theme-warning));
}

#site_toolbar .toolbar_main__status-dot {
  background: rgb(var(--v-theme-warning));
  border-radius: 50%;
  display: inline-block;
  height: 7px;
  width: 7px;
}

#site_toolbar .toolbar_right {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
}
</style>
